import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { SourceType } from '@/types/source';
import { useFeatureFlagsState } from '@/hooks';

export type TypeFilter = 'all' | SourceType.CAMERA | SourceType.FILE;

export type SortField = 'createdAt' | 'name';
export type SortDirection = 'asc' | 'desc';

export interface SortOption {
    field: SortField;
    direction: SortDirection;
}

export const NAME_SORT_OPTION: SortOption = {
    field: 'name',
    direction: 'asc',
};

export const RECENT_SORT_OPTION: SortOption = {
    field: 'createdAt',
    direction: 'desc',
};

export const getDefaultSortOption = (hdmiEnabled: boolean): SortOption =>
    hdmiEnabled ? NAME_SORT_OPTION : RECENT_SORT_OPTION;

export const SORT_OPTIONS: {
    id: string;
    field: SortField;
    direction: SortDirection;
    labelKey: string;
}[] = [
    {
        id: 'name-asc',
        field: 'name',
        direction: 'asc',
        labelKey: 'sort.nameAsc',
    },
    {
        id: 'name-desc',
        field: 'name',
        direction: 'desc',
        labelKey: 'sort.nameDesc',
    },
    {
        id: 'createdAt-desc',
        field: 'createdAt',
        direction: 'desc',
        labelKey: 'sort.newestFirst',
    },
    {
        id: 'createdAt-asc',
        field: 'createdAt',
        direction: 'asc',
        labelKey: 'sort.oldestFirst',
    },
];

interface SourcesFiltersState {
    typeFilter: TypeFilter;
    sortOption: SortOption | null;
    hasActiveFilters: boolean;
    setTypeFilter: (type: TypeFilter) => void;
    setSortOption: (option: SortOption | null) => void;
    clearAllFilters: () => void;
    getScopeParams: () => {
        type?: SourceType;
    };
    getQueryParams: () => {
        type?: SourceType;
        orderBy?: string;
    };
}

export function useSourcesFilters(): SourcesFiltersState {
    const { flags, isLoading: flagsLoading } = useFeatureFlagsState();
    const hdmiEnabled = flags.hdmiEnabled;
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
    const [sortOption, setSortOption] = useState<SortOption | null>(() =>
        flagsLoading ? null : getDefaultSortOption(hdmiEnabled),
    );
    const defaultAppliedRef = useRef(!flagsLoading);

    useEffect(() => {
        if (defaultAppliedRef.current || flagsLoading) return;
        defaultAppliedRef.current = true;
        setSortOption(getDefaultSortOption(hdmiEnabled));
    }, [flagsLoading, hdmiEnabled]);

    const hasActiveFilters = useMemo(() => {
        return typeFilter !== 'all';
    }, [typeFilter]);

    const clearAllFilters = useCallback(() => {
        setTypeFilter('all');
    }, []);

    const getScopeParams = useCallback(
        () => ({
            ...(typeFilter !== 'all' && {
                type: typeFilter as SourceType,
            }),
        }),
        [typeFilter],
    );

    const getQueryParams = useCallback(
        () => ({
            ...getScopeParams(),
            ...(sortOption && {
                orderBy: `${sortOption.field} ${sortOption.direction}`,
            }),
        }),
        [getScopeParams, sortOption],
    );

    return {
        typeFilter,
        sortOption,
        hasActiveFilters,
        setTypeFilter,
        setSortOption,
        clearAllFilters,
        getScopeParams,
        getQueryParams,
    };
}
